import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

// Auth + role utilities
import RequireAuth from "@/components/auth/RequireAuth";
import RequireRole from "@/components/auth/RequireRole";
import { dashboardFor } from "@/lib/roleRoute";

// Shells
import Layout from "@/components/Layout";
import PlatformLayout from "@/components/PlatformLayout";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// Auth + landing dashboards
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DoctorDashboard from "@/pages/DoctorDashboard";
import StaffDashboard from "@/pages/StaffDashboard";

// Platform
import PlatformDashboard from "@/pages/PlatformDashboard";
import PlatformHospitals from "@/pages/PlatformHospitals";
import PlatformAdmins from "@/pages/PlatformAdmins";
import PlatformDoctors from "@/pages/PlatformDoctors";
import PlatformUsers from "@/pages/PlatformUsers";

// Hospital pages
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Medicines from "@/pages/Medicines";
import NewPrescription from "@/pages/NewPrescription";
import PrescriptionView from "@/pages/PrescriptionView";
import PrescriptionHistory from "@/pages/PrescriptionHistory";
import PrintSettings from "@/pages/PrintSettings";
import BulkUpload from "@/pages/BulkUpload";
import Inventory from "@/pages/Inventory";
import Followups from "@/pages/Followups";
import Referrals from "@/pages/Referrals";
import ReferralView from "@/pages/ReferralView";
import ReferralPartners from "@/pages/ReferralPartners";
import Profile from "@/pages/Profile";
import Users from "@/pages/Users";
import Reception from "@/pages/Reception";
import Reservations from "@/pages/Reservations";
import DoctorCalendar from "@/pages/DoctorCalendar";
import PatientQR from "@/pages/PatientQR";
import PatientCard from "@/pages/PatientCard";
import KioskRegister from "@/pages/KioskRegister";
import KioskCodes from "@/pages/KioskCodes";
import PatientAccess from "@/pages/PatientAccess";
import DoctorGroups from "@/pages/DoctorGroups";
import DoctorBusiness from "@/pages/DoctorBusiness";
import POS from "@/pages/POS";
import POSTransaction from "@/pages/POSTransaction";
import POSServices from "@/pages/POSServices";
import POSReports from "@/pages/POSReports";
import QueueDisplay from "@/pages/QueueDisplay";
import UserManual from "@/pages/UserManual";
import PublicBooking from "@/pages/PublicBooking";
import BookingHome from "@/pages/booking/BookingHome";
import BookingWizard from "@/pages/booking/BookingWizard";
import BookingPayment from "@/pages/booking/BookingPayment";
import BookingConfirmed from "@/pages/booking/BookingConfirmed";
import HospitalPaymentQrSettings from "@/pages/HospitalPaymentQrSettings";
import POSReceiptSettings from "@/pages/POSReceiptSettings";
import DoctorScheduleSettings from "@/pages/DoctorScheduleSettings";
import BrandingSettings from "@/pages/BrandingSettings";

// Doctor mobile / PWA pages
import DoctorMobile from "@/pages/DoctorMobile";
import DoctorMobilePatient from "@/pages/DoctorMobilePatient";
import DoctorMobilePatients from "@/pages/DoctorMobilePatients";
import ReceptionMobile from "@/pages/ReceptionMobile";

// Dental
import DentalDashboard from "@/pages/dental/DentalDashboard";
import DentalPatientChart from "@/pages/dental/DentalPatientChart";
import DentalRecalls from "@/pages/dental/DentalRecalls";

const HOSPITAL_ROLES = ["admin", "hospital_admin", "doctor", "staff", "reception"];

function isMobileDevice() {
  if (typeof window === "undefined") return false;
  return /android|iphone|ipad|ipod/i.test(window.navigator.userAgent || "") || window.innerWidth < 768;
}

function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

// "/" → role-aware redirect. For doctors on mobile/PWA, send them to /doctor-mobile.
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const role = user.role;
  const clinicType = user.clinic_type || "medical";

  // A dental tenant has its own space, and it is tablet-first already — so it
  // wins over the phone-specific medical views rather than competing with them.
  if (clinicType === "dental") {
    const dentalDest = dashboardFor({ role, clinicType });
    return <Navigate to={dentalDest || "/login"} replace />;
  }

  const onMobile = isStandalonePWA() || isMobileDevice();
  if (role === "doctor" && onMobile) {
    return <Navigate to="/doctor-mobile" replace />;
  }
  if (role === "reception" && onMobile) {
    return <Navigate to="/reception-mobile" replace />;
  }

  const dest = dashboardFor({ role, clinicType });
  return <Navigate to={dest || "/login"} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/book" element={<BookingHome />} />
          <Route path="/book/:slug" element={<BookingWizard />} />
          <Route path="/book/:slug/payment/:rid" element={<BookingPayment />} />
          <Route path="/book/:slug/confirmed/:rid" element={<BookingConfirmed />} />
          <Route path="/book-legacy/:slug" element={<PublicBooking />} />
          <Route path="/patient-card/:token" element={<PatientCard />} />
          <Route path="/kiosk/:slug" element={<KioskRegister />} />
          <Route path="/kiosk/:slug/:doctorId" element={<KioskRegister />} />

          {/* Authenticated tree */}
          <Route element={<RequireAuth />}>
            {/* Platform admin only */}
            <Route element={<RequireRole allow={["platform_admin"]} />}>
              <Route element={<PlatformLayout />}>
                <Route path="/platform" element={<PlatformDashboard />} />
                <Route path="/platform/dashboard" element={<PlatformDashboard />} />
                <Route path="/platform/hospitals" element={<PlatformHospitals />} />
                <Route path="/platform/admins" element={<PlatformAdmins />} />
                <Route path="/platform/doctors" element={<PlatformDoctors />} />
                <Route path="/platform/users" element={<PlatformUsers />} />
                <Route path="/platform/manual" element={<UserManual />} />
              </Route>
            </Route>

            {/* Hospital users inside the hospital Layout */}
            <Route element={<Layout />}>
              {/* Strict role dashboards */}
              <Route element={<RequireRole allow={["admin", "hospital_admin"]} />}>
                <Route path="/admin/dashboard" element={<Dashboard />} />
              </Route>
              <Route element={<RequireRole allow={["doctor"]} />}>
                <Route path="/doctor/dashboard" element={<DoctorDashboard />} />
              </Route>
              <Route element={<RequireRole allow={["staff", "reception"]} />}>
                <Route path="/staff/dashboard" element={<StaffDashboard />} />
              </Route>

              {/* Shared hospital pages */}
              <Route element={<RequireRole allow={HOSPITAL_ROLES} />}>
                <Route path="/patients" element={<Patients />} />
                <Route path="/patients/bulk-upload" element={<BulkUpload kind="patients" />} />
                <Route path="/patients/:id" element={<PatientDetail />} />
                <Route path="/medicines" element={<Medicines />} />
                <Route path="/medicines/bulk-upload" element={<BulkUpload kind="medicines" />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/followups" element={<Followups />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/referrals/:id/preview" element={<ReferralView />} />
                <Route path="/referral-partners" element={<ReferralPartners />} />
                <Route path="/prescriptions" element={<PrescriptionHistory />} />
                <Route path="/prescriptions/:id/preview" element={<PrescriptionView />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/manual" element={<UserManual />} />
                <Route path="/queue-screen" element={<QueueDisplay />} />
                <Route path="/queue-display" element={<QueueDisplay />} />
              </Route>

              {/* Reception / clinic-front desk */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "reception"]} />}>
                <Route path="/reception" element={<Reception />} />
                <Route path="/kiosk-codes" element={<KioskCodes />} />
              </Route>

              {/* Reservations + calendars (admin/doctor/reception) */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor", "reception"]} />}>
                <Route path="/reservations" element={<Reservations />} />
                <Route path="/doctor-calendar" element={<DoctorCalendar />} />
                <Route path="/patient-qr" element={<PatientQR />} />
                <Route path="/pos" element={<POS />} />
                <Route path="/pos/transactions/:id" element={<POSTransaction />} />
                <Route path="/pos/services" element={<POSServices />} />
                <Route path="/pos/reports" element={<POSReports />} />
              </Route>

              {/* Admin-only */}
              <Route element={<RequireRole allow={["admin", "hospital_admin"]} />}>
                <Route path="/patient-access" element={<PatientAccess />} />
                <Route path="/doctor-groups" element={<DoctorGroups />} />
                <Route path="/users" element={<Users />} />
                <Route path="/settings/branding" element={<BrandingSettings />} />
                <Route path="/settings/receipt" element={<POSReceiptSettings />} />
              </Route>

              {/* Doctor-capable (legacy doctor-dashboard, business, print settings) */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor"]} />}>
                <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
                <Route path="/doctor-business" element={<DoctorBusiness />} />
                <Route path="/prescriptions/new" element={<NewPrescription />} />
                <Route path="/prescriptions/:id/edit" element={<NewPrescription />} />
                <Route path="/settings" element={<PrintSettings />} />
                <Route path="/settings/payment-qr" element={<HospitalPaymentQrSettings />} />
              </Route>

              {/* Dental space — a dental clinic is its own hospital tenant */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor"]} />}>
                <Route path="/dental" element={<DentalDashboard />} />
                <Route path="/dental/patients/:id/chart" element={<DentalPatientChart />} />
              </Route>
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor", "reception", "staff"]} />}>
                <Route path="/dental/recalls" element={<DentalRecalls />} />
              </Route>

              {/* Doctor schedule — doctors edit their own; admins & reception manage any */}
              <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor", "reception"]} />}>
                <Route path="/doctor/schedule" element={<DoctorScheduleSettings />} />
              </Route>
            </Route>

            {/* Doctor mobile / PWA — FULL SCREEN, no sidebar Layout */}
            <Route element={<RequireRole allow={["admin", "hospital_admin", "doctor"]} />}>
              <Route path="/doctor-mobile" element={<DoctorMobile />} />
              <Route path="/doctor-mobile/patient/:id" element={<DoctorMobilePatient />} />
              <Route path="/doctor-mobile/patients" element={<DoctorMobilePatients />} />
            </Route>

            {/* Reception mobile / PWA — FULL SCREEN, no sidebar Layout */}
            <Route element={<RequireRole allow={["admin", "hospital_admin", "reception"]} />}>
              <Route path="/reception-mobile" element={<ReceptionMobile />} />
            </Route>
          </Route>

          {/* Root + catch-all → role-aware redirect */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
